import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, Users, Briefcase, Ban, Star, BarChart3, Settings, LogOut, UserCircle, ChevronLeft, ChevronRight, MessageCircle, ClipboardList, ChevronDown, Receipt, CreditCard, FileText, Phone, ThumbsUp, Smartphone, HardDrive, Activity, Award, Package as PackageIcon, Pill, Layers, ShieldCheck, CalendarDays, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { useAccessMode } from '../context/AccessModeContext';

const Layout = ({ user, onLogout, children }) => {
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(
    location.pathname === '/reports' || location.pathname === '/job-card-reports' || location.pathname === '/teller-reports' || location.pathname === '/service-reports' || location.pathname === '/ebitda' || location.pathname === '/incentives'
  );
  const [feedbackExpanded, setFeedbackExpanded] = useState(
    location.pathname === '/review' || location.pathname === '/no-show'
  );
  const [rewardsExpanded, setRewardsExpanded] = useState(
    location.pathname.startsWith('/rewards')
  );
  const [whatsappExpanded, setWhatsappExpanded] = useState(
    location.pathname === '/whatsapp' || location.pathname === '/whatsapp-reminders'
  );
  const [crmExpanded, setCrmExpanded] = useState(
    location.pathname.startsWith('/crm')
  );
  const submenuStates = {
    reports:  [reportsExpanded,  setReportsExpanded],
    feedback: [feedbackExpanded, setFeedbackExpanded],
    rewards:  [rewardsExpanded,  setRewardsExpanded],
    whatsapp: [whatsappExpanded, setWhatsappExpanded],
    crm:      [crmExpanded,      setCrmExpanded],
  };

  // Define all nav items
  const allNavItems = [
    { path: '/', icon: Calendar, label: 'Dashboard', color: 'text-sky-600' },
    { path: '/calendar', icon: CalendarDays, label: 'Calendar', color: 'text-purple-600', allowedRoles: ['Admin', 'FrontDeskExecutive'] },
    { path: '/patients', icon: Users, label: 'Patients', color: 'text-emerald-600' },
    { path: '/services', icon: Briefcase, label: 'Services', color: 'text-purple-600', adminOnly: true },
    { path: '/admin/services-audit', icon: Layers, label: 'Services Audit', color: 'text-amber-600', adminOnly: true },
    { path: '/job-cards', icon: ClipboardList, label: 'Job Cards', color: 'text-orange-600', allowedRoles: ['Admin', 'Teller'] },
    { path: '/followup-calls', icon: Phone, label: 'Followup Calls', color: 'text-blue-600', adminOnly: true },
    { path: '/blackouts', icon: Ban, label: 'Blackout Days', color: 'text-rose-600', adminOnly: true },
    { 
      path: '/review', 
      icon: ThumbsUp, 
      label: 'Feedback', 
      color: 'text-cyan-600',
      adminOnly: true,
      hasSubmenu: true,
      submenuKey: 'feedback',
      submenu: [
        { path: '/review', label: 'Reviews', icon: Star },
        { path: '/patient-feedback', label: 'Patient Feedback', icon: ThumbsUp },
        { path: '/no-show', label: 'No Shows', icon: Ban }
      ]
    },
    { 
      path: '/whatsapp', 
      icon: MessageCircle, 
      label: 'WhatsApp', 
      color: 'text-green-600', 
      adminOnly: true,
      hasSubmenu: true,
      submenuKey: 'whatsapp',
      submenu: [
        { path: '/whatsapp-reminders', label: '📲 Reminders Monitor' },
        { path: '/whatsapp-guide', label: '📋 Template Guide' },
        { path: '/whatsapp-templates', label: '📨 Live Templates & Logs' }
      ]
    },
    { 
      path: '/reports', 
      icon: BarChart3, 
      label: 'Reports', 
      color: 'text-indigo-600', 
      adminOnly: true,
      hasSubmenu: true,
      submenuKey: 'reports',
      submenu: [
        { path: '/analytics', label: '📊 Analytics Dashboard' },
        { path: '/service-reports', label: '📋 Service Reports' },
        { path: '/ebitda', label: '💰 EBITDA' },
        { path: '/incentives', label: '🏆 Incentives' },
        { path: '/reports', label: 'General Reports' },
        { path: '/job-card-reports', label: 'Job Card Reports' },
        { path: '/teller-reports', label: 'Teller Reports' }
      ]
    },
    { path: '/teller', icon: Receipt, label: 'Teller POS', color: 'text-green-600', adminOnly: true },
    { path: '/teller-config', icon: CreditCard, label: 'Teller Config', color: 'text-green-600', adminOnly: true },
    { path: '/packages', icon: PackageIcon, label: 'Packages', color: 'text-purple-600', adminOnly: true },
    { path: '/pharmacy-sales', icon: Pill, label: 'Pharmacy Sales', color: 'text-teal-600', adminOnly: true },
    {
      path: '/rewards/services',
      icon: Award,
      label: 'Rewards',
      color: 'text-amber-600',
      adminOnly: true,
      hasSubmenu: true,
      submenuKey: 'rewards',
      submenu: [
        { path: '/rewards/services', label: 'Reward Services' },
        { path: '/rewards/settings', label: 'Reward Settings' },
        { path: '/rewards/reports',  label: 'Reward Reports'  },
      ],
    },
    { path: '/app-users', icon: Smartphone, label: 'App Users', color: 'text-indigo-600', adminOnly: true },
    { path: '/backups', icon: HardDrive, label: 'Backups', color: 'text-slate-700', adminOnly: true },
    { path: '/system-logs', icon: Activity, label: 'System Logs', color: 'text-rose-600', adminOnly: true },
    { path: '/access-logs', icon: ShieldCheck, label: 'Access Log', color: 'text-cyan-700', adminOnly: true },
    { path: '/users', icon: Settings, label: 'Users', color: 'text-gray-600', adminOnly: true },
    { path: '/settings', icon: Settings, label: 'App Settings', color: 'text-slate-700', adminOnly: true },
  ];

  const { eclipse, setEclipse, saving: eclipseSaving } = useAccessMode();

  const filteredNavItems = allNavItems.filter(item => {
    if (item.adminOnly && user.role !== 'Admin') return false;
    if (Array.isArray(item.allowedRoles) && !item.allowedRoles.includes(user.role)) return false;
    if (eclipse && user.role === 'FrontDeskExecutive' && item.path === '/') return false;
    return true;
  });

  const eclipseToggle = user?.role === 'Admin' ? (
    <label className={`flex items-center gap-2 rounded-full px-3 py-1.5 border transition-colors cursor-pointer select-none ${eclipse ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-gray-200 text-gray-700'}`} data-testid="eclipse-toggle-wrap" title="Eclipse: sign out tellers, limit front desk to Calendar & Patients">
      <Moon className={`w-4 h-4 ${eclipse ? 'text-amber-300' : 'text-gray-500'}`} />
      <span className="text-xs font-semibold">Eclipse {eclipse ? 'ON' : 'OFF'}</span>
      <Switch checked={eclipse} disabled={eclipseSaving} onCheckedChange={setEclipse} data-testid="eclipse-toggle" className="scale-90" />
    </label>
  ) : null;


  // ==================== FEEDBACK REVIEWER LAYOUT ====================
  if (user?.role === 'FeedbackReviewer') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-2 rounded-xl">
                <ThumbsUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Feedback Review</h1>
                <p className="text-xs text-gray-500">{user.name}</p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="text-gray-600"
              data-testid="feedback-reviewer-logout"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>
        <main className="pb-safe">
          {children}
        </main>
      </div>
    );
  }

  // ==================== TELLER MOBILE LAYOUT ====================
  if (user?.role === 'Teller') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Mobile Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-2 rounded-xl">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{location.pathname.startsWith('/job-cards') ? 'Job Cards' : 'Teller'}</h1>
                <p className="text-xs text-gray-500">{user.name}</p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="text-gray-600"
              data-testid="teller-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Content */}
        <main className="pb-24">
          {children}
        </main>

        {/* Bottom tab bar — thumb-reachable on phones */}
        <nav className="fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]" data-testid="teller-bottom-nav">
          <div className="grid grid-cols-2 max-w-lg mx-auto">
            {[
              { path: '/teller', label: 'POS', icon: Receipt, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { path: '/job-cards', label: 'Job Cards', icon: ClipboardList, color: 'text-orange-600', bg: 'bg-orange-50' },
            ].map(({ path, label, icon: Icon, color, bg }) => {
              const active = location.pathname.startsWith(path);
              return (
                <Link
                  key={path}
                  to={path}
                  className={`flex flex-col items-center justify-center py-2.5 gap-0.5 text-xs font-medium transition-colors ${active ? color : 'text-gray-500'}`}
                  data-testid={`teller-nav-${label.toLowerCase().replace(' ', '-')}`}
                >
                  <span className={`px-4 py-1 rounded-full ${active ? bg : ''}`}><Icon className="w-5 h-5" /></span>
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    );
  }

  // ==================== THERAPIST MOBILE LAYOUT ====================
  if (user?.role === 'Therapist') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Mobile Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-xl">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Job Cards</h1>
                <p className="text-xs text-gray-500">{user.name}</p>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="text-gray-600"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Content */}
        <main className="pb-safe">
          {children}
        </main>
      </div>
    );
  }

  // ==================== ADMIN/FRONT DESK LAYOUT ====================
  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`${isSidebarCollapsed ? 'w-20' : 'w-64'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 relative`}>
        {/* Toggle button */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute -right-3 top-6 z-10 bg-white border border-gray-200 rounded-full p-1 hover:bg-gray-50 shadow-md transition-all"
          data-testid="sidebar-toggle"
        >
          {isSidebarCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          )}
        </button>

        <div className="p-6 border-b border-gray-200">
          <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-xl flex-shrink-0">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div>
                <h1 className="text-xl font-bold text-gray-900">Clinic Manager</h1>
                <p className="text-xs text-gray-500">Appointment System</p>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.submenu && item.submenu.some(sub => location.pathname === sub.path));
            
            // Handle items with submenu (like Reports, Feedback, Rewards, WhatsApp)
            if (item.hasSubmenu && !isSidebarCollapsed) {
              const [isExpanded, setExpanded] = submenuStates[item.submenuKey] || [false, () => {}];
              const toggleExpanded = () => setExpanded(!isExpanded);
              
              return (
                <div key={item.path}>
                  <button
                    onClick={toggleExpanded}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition ${
                      isActive
                        ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-5 h-5 ${isActive ? item.color : ''} flex-shrink-0`} />
                      <span>{item.label}</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {isExpanded && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.submenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition ${
                              location.pathname === subItem.path
                                ? 'bg-purple-100 text-purple-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {SubIcon && <SubIcon className="w-4 h-4" />}
                            {subItem.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Handle items with submenu when sidebar is collapsed
            if (item.hasSubmenu && isSidebarCollapsed) {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={item.label}
                  className={`flex items-center justify-center px-4 py-3 rounded-xl transition ${
                    isActive
                      ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? item.color : ''} flex-shrink-0`} />
                </Link>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                title={isSidebarCollapsed ? item.label : ''}
                className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-xl transition ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? item.color : ''} flex-shrink-0`} />
                {!isSidebarCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-200">
          <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-2 rounded-full flex-shrink-0">
              <UserCircle className="w-6 h-6 text-gray-600" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.role}</p>
              </div>
            )}
          </div>
          {!isSidebarCollapsed && eclipseToggle && <div className="mt-3 hidden lg:block">{eclipseToggle}</div>}
          <Button
            onClick={onLogout}
            variant="ghost"
            className={`w-full mt-3 text-gray-600 hover:text-red-600 hover:bg-red-50 ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'}`}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!isSidebarCollapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto flex flex-col">
        {/* Mobile top bar (admin): brand + Eclipse toggle */}
        {eclipseToggle && (
          <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between" data-testid="admin-mobile-topbar">
            <span className="text-sm font-semibold text-gray-900 truncate">{user.name}</span>
            {eclipseToggle}
          </div>
        )}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
